import { Component } from 'react';
import { Cell } from '@table-library/react-table-library/table';
import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import Loading from '../../../../components/Loading';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { comparisonColumns, contributorColumns } from '../../GscTools/columns';
import { reportsPosition } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { sourceLabel } from '../sources';

const FILTERS = ['all', 'gains', 'declines'];

/**
 * Block 03 — the pages behind the selected source, and how each one moved.
 *
 * Two windows of page rows joined on the exact URL Search Console returned. A page present in only
 * one of them has no change: it is shown as not comparable rather than as new traffic or a 100%
 * gain, because Search Console withholding a row is not the same as the page having had none.
 * The order is by the size of the click movement, either way, with the rows that cannot be
 * compared last — this ranks changes, it does not call them wins or losses.
 *
 * Without a baseline to join against — no comparison chosen, or the baseline pages failing to come
 * back — the current window's rows are still worth reading, so they are shown on their own with
 * the change columns left out rather than filled with "not comparable" in every cell.
 *
 * Any row opens its own daily history in the block below: the same source, the same scope, plus an
 * exact filter on that URL. There is no query view to drill into — Discover and Google News report
 * no query dimension at all.
 */
class PageChanges extends Component {
    constructor(props) {
        super(props);
        this.searchFunction = this.searchFunction.bind(this);
        this.additionalBox = this.additionalBox.bind(this);
        this.actionColumn = this.actionColumn.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    searchFunction(data, search) {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((item) => item.key?.toLowerCase().includes(term));
    }

    /** Clicking the row does what its button does, including toggling back off. */
    handleRowClick(item) {
        const { selectedKey, onSelect } = this.props;
        onSelect(item.key === selectedKey ? null : item.key);
    }

    /** All / Gains / Declines, over the joined rows in memory — switching costs no request. */
    additionalBox() {
        const { t, filter, onFilter, comparing } = this.props;
        if (!comparing) return null;

        return (
            <div className="table-additional-view">
                <div className="table-additional-view-btns">
                    <div className="table-additional-view-btn-groups">
                        {FILTERS.map((name) => (
                            <button
                                type="button"
                                key={name}
                                className={filter === name ? 'active' : ''}
                                aria-pressed={filter === name}
                                onClick={() => onFilter(name)}
                            >
                                {t(`tools.gsc.filters.${name}`)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    /** The button that loads one page's daily history into the block below. */
    actionColumn() {
        const { t, selectedKey, onSelect } = this.props;

        return {
            label: t('tools.discover_news.pages.trend_column'),
            key: 'trend',
            data: 'key',
            sortType: (array) => array,
            cellSize: 'minmax(130px, 150px)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (csvDownload) return '';

                const picked = item.key === selectedKey;

                return (
                    <div className="d-flex">
                        <button
                            type="button"
                            className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                            aria-pressed={picked}
                            onClick={() => onSelect(picked ? null : item.key)}
                        >
                            {picked && <i className="icon-check-icon" aria-hidden="true" />}
                            {picked
                                ? t('tools.discover_news.pages.trend_showing')
                                : t('tools.discover_news.pages.trend_action')}
                        </button>
                    </div>
                );
            },
        };
    }

    visibleRows() {
        const { rows, filter, comparing } = this.props;
        if (!comparing) return rows;

        if (filter === 'gains') return rows.filter((row) => row.deltaClicks > 0);
        if (filter === 'declines') return rows.filter((row) => row.deltaClicks < 0);
        return rows;
    }

    render() {
        const {
            t,
            scope,
            status,
            error,
            baselineError,
            comparing,
            period,
            shortened,
            stopReason,
            selectedKey,
            loadingMore,
            onLoadMore,
        } = this.props;

        const hasPosition = reportsPosition(scope.searchType);
        const keyLabel = t('tools.gsc.columns.page');
        const columns = comparing
            ? comparisonColumns(t, { hasPosition, keyLabel, isUrl: true })
            : contributorColumns(t, { hasPosition, keyLabel, isUrl: true });
        const tableColumns = [...columns, this.actionColumn()];
        const source = sourceLabel(t, scope.searchType);

        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-header d-flex justify-content-between">
                    <div>
                        <p className="card-title">{t('tools.discover_news.pages.title')}</p>
                        <p className="gsc-tool-sub">
                            {t('tools.discover_news.pages.description', { source })}
                            {!!period && <> · {formatPeriod(period)}</>}
                        </p>
                        <p className="gsc-tool-rows-hint">
                            <i className="icon-info" aria-hidden="true" />
                            {t('tools.discover_news.pages.select_hint')}
                        </p>
                    </div>
                </div>

                <div className="card-body">
                    {shortened && (
                        <div className="gsc-tool-banner">
                            {t('tools.discover_news.pages.shortened')}
                        </div>
                    )}

                    {(status === 'idle' || status === 'loading') && <Loading padding={20} />}

                    {status === 'error' && (
                        <div className="gsc-tool-banner error">
                            {error ?? t('global.something_went_wrong')}
                        </div>
                    )}

                    {status === 'done' && (
                        <>
                            {/* Said above the rows rather than instead of them: the baseline
                                failing takes the changes away, not the current window's pages. */}
                            {!!baselineError && (
                                <div className="gsc-tool-banner error">
                                    {t('tools.discover_news.pages.baseline_failed', {
                                        reason: baselineError,
                                    })}
                                </div>
                            )}

                            {!!error && <div className="gsc-tool-banner error">{error}</div>}

                            {!scope.baseline && (
                                <div className="gsc-tool-banner">
                                    {t('tools.discover_news.pages.no_comparison')}
                                </div>
                            )}

                            <DataTable
                                nodes={this.visibleRows()}
                                COLUMNS={tableColumns}
                                customColumnsForExport={columns}
                                sortFns={getSortKeys(tableColumns)}
                                additionalStyles={setAdditionalStyles(tableColumns)}
                                searchFunction={this.searchFunction}
                                additionalBox={this.additionalBox}
                                fileName={`${t('tools.discover_news.pages.title')} — ${source}`}
                                customEmptyMessage={t('tools.discover_news.pages.empty', {
                                    source,
                                })}
                                customContainerStyles="gsc-tool-key-table"
                                rowClassName={(item) =>
                                    item.key === selectedKey ? 'dt-row-picked' : ''
                                }
                                onRowClick={this.handleRowClick}
                                loadMore={onLoadMore}
                                loadMoreLoading={loadingMore}
                                showLoadMoreBtn={stopReason === 'more_available'}
                            />

                            <p className="gsc-tool-table-note">
                                {t(`tools.gsc.stop.${stopReason}`)}{' '}
                                {t('tools.discover_news.pages.subset')}
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(PageChanges);
